import { Note, NoteWithExtraInfo } from '@oneview/cards/NotesCard/types';
import { Scope } from '@oneview/constants/lookups';
import { createQueryFn } from '@oneview/redux/utils';
import { showToast } from '@/redux';
import { api } from './baseApi';
import { DataTags } from './dataTags';

export const noteApi = api.injectEndpoints({
    endpoints: (build) => ({
        getNotes: build.query({
            queryFn: createQueryFn(
                async (
                    {
                        id,
                        scope,
                    }: {
                        id: number;
                        scope: Scope.User | Scope.Account;
                    },
                    { extra: api },
                ) => {
                    const response = await (scope === Scope.User
                        ? api.orbisSdk.get<Note[]>('v2/advisory/users/notes', {}, { params: { uid: id } })
                        : api.orbisSdk.get<Note[]>('v2/advisory/users/account/notes', {}, { params: { aid: id } }));
                    return response.data;
                },
            ),
            providesTags: [DataTags.GetNotes],
        }),
        getAllAccountsNotes: build.query({
            queryFn: createQueryFn(async (accounts: { accountId: number; accountNumber: string }[], { extra: api }) => {
                const response = await Promise.all(
                    accounts.map((account) =>
                        api.orbisSdk.get<Note[]>(
                            'v2/advisory/users/account/notes',
                            {},
                            { params: { aid: account.accountId } },
                        ),
                    ),
                );

                const result: NoteWithExtraInfo[] = response
                    .map((item, index) =>
                        item.data.map((note) => ({
                            ...note,
                            accountId: accounts[index]?.accountId,
                            accountNumber: accounts[index]?.accountNumber,
                        })),
                    )
                    .flat();

                return result;
            }),
            providesTags: [DataTags.GetAllAccountsNotes],
        }),
        createNote: build.mutation({
            queryFn: createQueryFn(
                async (
                    {
                        id,
                        content,
                        scope,
                    }: {
                        id: number;
                        content: string;
                        scope: Scope.User | Scope.Account;
                    },
                    { extra: api },
                ) => {
                    const response = await (scope === Scope.User
                        ? api.orbisSdk.post<Note>(
                              'v2/advisory/users/notes',
                              { userId: id, content },
                              { urlQuery: 'add' },
                          )
                        : api.orbisSdk.post<Note>(
                              'v2/advisory/users/account/notes',
                              { accountId: id, content },
                              { urlQuery: 'add' },
                          ));

                    return response.data;
                },
                { pendingMessage: true },
            ),
            invalidatesTags: [DataTags.GetNotes, DataTags.GetAllAccountsNotes],
        }),
        modifyNote: build.mutation({
            queryFn: createQueryFn(
                async (
                    {
                        noteId,
                        content,
                        scope,
                        accountId,
                    }: {
                        noteId: number;
                        content: string;
                        scope: Scope.User | Scope.Account;
                        accountId?: number;
                    },
                    { extra: api },
                ) => {
                    const response = await (scope === Scope.User
                        ? api.orbisSdk.post<Note>(
                              'v2/advisory/users/notes',
                              { noteId, content },
                              { urlQuery: 'modify' },
                          )
                        : api.orbisSdk.post<Note>(
                              'v2/advisory/users/account/notes',
                              { accountId, noteId, content },
                              { urlQuery: 'modify' },
                          ));
                    return response.data;
                },
                { pendingMessage: true },
            ),
            invalidatesTags: [DataTags.GetNotes, DataTags.GetAllAccountsNotes],
        }),
        deleteNote: build.mutation({
            queryFn: createQueryFn(
                async (
                    {
                        noteId,
                        scope,
                        accountId,
                    }: {
                        noteId: number;
                        scope: Scope.User | Scope.Account;
                        accountId?: number;
                    },
                    { extra: api, dispatch },
                ) => {
                    if (scope === Scope.User) {
                        await api.orbisSdk.post('v2/advisory/users/notes', { noteId }, { urlQuery: 'delete' });
                    } else {
                        await api.orbisSdk.post(
                            'v2/advisory/users/account/notes',
                            { accountId, noteId },
                            { urlQuery: 'delete' },
                        );
                    }

                    dispatch(showToast('Note deleted'));
                },
                { pendingMessage: true },
            ),
            invalidatesTags: [DataTags.GetNotes, DataTags.GetAllAccountsNotes],
        }),
    }),
});

export const {
    useGetNotesQuery,
    useGetAllAccountsNotesQuery,
    useCreateNoteMutation,
    useModifyNoteMutation,
    useDeleteNoteMutation,
} = noteApi;
